import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    Card,
    CrudMode,
    Dialog,
    handleClose,
    Page,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import { DisciplineViewResource, DisciplineEditResource, Discipline, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { DisciplinePageBody } from "./DisciplinePageBody";
import { DisciplinePageHeader } from "./DisciplinePageHeader";

const routeName = "discipline";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const disciplinePageRoute: PageRoute<Params> = {
    handle: "Discipline",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const DisciplinePage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);

    const [discipline, setDiscipline] = useState<Discipline>({} as Discipline);
    const [initDiscipline, setInitDiscipline] = useState<Discipline>(discipline);
    const [viewResource, setViewResource] = useState<DisciplineViewResource>({});
    const [editResource, setEditResource] = useState<DisciplineEditResource>({});

    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [discipline] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);
        ApiService.disciplines.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.disciplines.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${disciplinePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.disciplines.get(id).then((response) => {
            setDiscipline(response);
            setInitDiscipline(response);
            resetUnsavedChanges([response]);
        });
    };

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={
                <DisciplinePageHeader
                    rootPermission={ROOT_PERMISSION}
                    handle={disciplinePageRoute.handle}
                    crudMode={params.crudMode}
                    reroute={reroute}
                    fetch={fetch}
                    discipline={discipline}
                    setDiscipline={setDiscipline}
                    initDiscipline={initDiscipline}
                    setValidationErrors={setValidationErrors}
                    unsavedChanges={unsavedChanges}
                    resetUnsavedChanges={resetUnsavedChanges}
                />
            }
            content={
                <DisciplinePageBody
                    discipline={discipline}
                    setDiscipline={setDiscipline}
                    viewResource={viewResource}
                    editResource={editResource}
                    crudMode={params.crudMode}
                    validationErrors={validationErrors}
                />
            }
        />
    );
};